import ConfirmationDialog, {
  ConfirmationDialogButtonColor
} from "@components/ConfirmationDialog";
import CustomSnackBar from "@components/CustomSnackBar";
import ModelVarsCsvReader from "@components/ModelVarsCsvReader";
import ExternalVarCell from "@components/Table/ExternalVarCell";
import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState,
  setSuccessAlert,
  setWarningAlert
} from "@constants/alerts";
import { BUTTON_PRIMARY_ICON_FONT_SIZE } from "@constants/button";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from "@mui/material";
import {
  useClientModelExternalEffects,
  useClientModelVariables,
  useClients,
  useClientsModels,
  usePatchClientModelInputs
} from "@services/api";
import {
  ClientModel,
  ClientModelExternalEffect,
  ClientModelVariable,
  ClientShort,
  CustomClientModelVariable
} from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import { getCurrentDateWeek } from "@utils/date";
import {
  externalEffectsToClientInputs,
  getExternalEffectsValues,
  getExternalVarsPredefinedValues,
  IVariableValues
} from "@utils/external_effects";
import AdminPanelNavBar from "@views/ViewAdminPanel/components/AdminPanelNavBar";
import ClientModelSelector from "@views/ViewAdminPanel/components/ClientModelSelector";
import ClientSelector from "@views/ViewAdminPanel/components/ClientSelector";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ExternalEffectsColumn,
  TABLE_BODY_CELL_FONT_SIZE,
  TABLE_HEADER_CELL_FONT_SIZE
} from "./ExternalEffectsTableTypes";

interface ConfirmationDialogStatus {
  open: boolean;
  title: string;
  buttonColor: ConfirmationDialogButtonColor;
  operation: "save" | "discard";
  confirmButtonText?: string;
}

const initExtVariableValues = (
  extVariableValues: IVariableValues,
  clientExternalEffects?: ClientModelExternalEffect,
  modelVars?: ClientModelVariable[]
) => {
  const timePeriods = clientExternalEffects?.time_periods || [];
  const clientInputs = clientExternalEffects?.model_inputs || [];

  const predefinedValues = getExternalVarsPredefinedValues(clientInputs);

  return getExternalEffectsValues(
    timePeriods,
    extVariableValues,
    predefinedValues,
    modelVars
  );
};

const ViewClientExternalEffects = () => {
  const { t } = useTranslation();

  const [selectedClient, setSelectedClient] = useState<string>("");
  const [selectedClientModel, setSelectedClientModel] = useState<string>("");
  const [extVariableValues, setExtVariableValues] = useState<IVariableValues>(
    {}
  );
  const [_, setModelVars] = useState<CustomClientModelVariable[]>([]);
  const [confirmationDialogStatus, setConfirmationDialogStatus] =
    useState<ConfirmationDialogStatus>({
      open: false,
      title: "",
      buttonColor: "error",
      operation: "discard",
      confirmButtonText: undefined
    });
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });
  const [isSaving, setIsSaving] = useState(false);
  const { data: clients, isFetching: isLoading } = useClients();
  const mutationPatchClientInputs = usePatchClientModelInputs();
  const queryClient = useQueryClient();

  const clientModelsQueries = useClientsModels(
    (clients || []).map((client) => client.id.toString())
  );

  const currentYear = useMemo(() => {
    return new Date().getFullYear();
  }, []);

  const currentWeek = useMemo(() => {
    return getCurrentDateWeek();
  }, []);

  const { data: clientExternalEffects, isFetching: isLoadingExternalEffects } =
    useClientModelExternalEffects(selectedClientModel, currentYear);

  const { data: modelVars, isFetching: isLoadingExtVars } =
    useClientModelVariables(Number(selectedClientModel), "external");

  const onClientSelectChange = (clientId: string) => {
    setSelectedClient(clientId.toString());
    const clientModels = clientModelsMapping[clientId];
    if (clientModels.length === 0) {
      setSnackbarAlert(
        setWarningAlert(
          t("clientExternalEffects.noModelsFound", {
            name: clientMapping[clientId].name
          })
        )
      );
    }
  };

  const onConfirmConfirmationDialog = () => {
    if (confirmationDialogStatus.operation === "save") {
      setIsSaving(true);
      const clientModelInputs = externalEffectsToClientInputs(
        Number(selectedClientModel),
        extVariableValues,
        modelVars || []
      );

      mutationPatchClientInputs.mutate(
        {
          model_id: selectedClientModel,
          updates: clientModelInputs
        },
        {
          onSuccess: () => {
            setSnackbarAlert(
              setSuccessAlert(t("clientExternalEffects.successSaving"))
            );
            queryClient.invalidateQueries({
              queryKey: [
                "client",
                "external_effects",
                selectedClientModel,
                currentYear
              ]
            });

            queryClient.invalidateQueries({
              queryKey: ["client", "inputs", selectedClientModel],
              exact: false
            });

            queryClient
              .refetchQueries({
                queryKey: [
                  "client",
                  "external_effects",
                  selectedClientModel,
                  currentYear
                ]
              })
              .finally(() => {
                setIsSaving(false);
              });
          },
          onError: () => {
            setIsSaving(false);
            setSnackbarAlert(
              setErrorAlert(t("clientExternalEffects.errorSaving"))
            );
          }
        }
      );
    } else {
      const values = initExtVariableValues(
        {},
        clientExternalEffects,
        modelVars
      );
      setExtVariableValues(values);
    }
    setConfirmationDialogStatus({
      open: false,
      title: "",
      buttonColor: "error",
      operation: "discard"
    });
  };

  const onCloseConfirmationDialog = () => {
    setConfirmationDialogStatus({
      open: false,
      title: "",
      buttonColor: "error",
      operation: "discard"
    });
  };

  const onDiscardClick = () => {
    setConfirmationDialogStatus({
      open: true,
      title: t("clientExternalEffects.discardConfirmation"),
      buttonColor: "error",
      confirmButtonText: undefined,
      operation: "discard"
    });
  };

  const onSaveClick = () => {
    setConfirmationDialogStatus({
      open: true,
      title: t("clientExternalEffects.saveConfirmation"),
      buttonColor: "success",
      confirmButtonText: "common.save",
      operation: "save"
    });
  };

  const isLoadingExternalData = useMemo(() => {
    return isLoadingExternalEffects || isLoadingExtVars;
  }, [isLoadingExternalEffects, isLoadingExtVars]);

  useEffect(() => {
    if (isLoadingExternalData) {
      return;
    }

    const values = initExtVariableValues(
      extVariableValues,
      clientExternalEffects,
      modelVars
    );

    setExtVariableValues(values);
  }, [setExtVariableValues, isLoading, modelVars, clientExternalEffects]);

  // Create mapping of clientId to list of models
  const { clientModelsMapping, modelMapping, clientMapping } = useMemo(() => {
    const mapping: Record<string, ClientModel[]> = {};
    const modelMapping: Record<string, ClientModel> = {};
    const clientMapping: Record<string, ClientShort> = {};

    clients?.forEach((client, index) => {
      const queryResult = clientModelsQueries[index];
      if (queryResult?.data) {
        mapping[client.id.toString()] = queryResult.data;
        queryResult.data.forEach((model) => {
          modelMapping[model.id.toString()] = model;
        });
        clientMapping[client.id.toString()] = client;
      }
    });

    return {
      clientModelsMapping: mapping,
      modelMapping: modelMapping,
      clientMapping
    };
  }, [clients, clientModelsQueries]);

  const columns: ExternalEffectsColumn[] = useMemo(() => {
    if (selectedClientModel.length === 0 || isLoadingExtVars) {
      return [];
    }
    const columns: ExternalEffectsColumn[] =
      (modelVars || []).map((externalVar) => ({
        id: externalVar.id.toString(),
        label: externalVar.name,
        minWidth: 100,
        align: "center"
      })) || [];
    return [
      {
        id: "time_period",
        label: t("clientExternalEffects.timePeriod"),
        minWidth: 100,
        align: "center"
      },
      ...columns
    ];
  }, [selectedClientModel, clientExternalEffects, modelVars]);

  const tableRows = useMemo(() => {
    const timePeriods = clientExternalEffects?.time_periods || [];
    const externalEffects = modelVars || [];

    return timePeriods.map((timePeriod) => {
      const aggregationType =
        modelMapping[selectedClientModel].aggregation_type;

      const weekNumber = parseInt(
        aggregationType === "weekly"
          ? timePeriod.split("W")[1]
          : timePeriod.split("M")[1] || "0"
      );
      const cells = externalEffects.map((externalVar) => {
        return (
          <ExternalVarCell
            key={`${selectedClientModel}-external-effect-var-${externalVar.id}-${timePeriod}`}
            componentId="client-external-effects"
            timePeriod={timePeriod}
            modelVar={externalVar}
            extVariableValues={extVariableValues}
            setExtVariableValues={setExtVariableValues}
            style={{ fontSize: TABLE_BODY_CELL_FONT_SIZE }}
            enabled={weekNumber > currentWeek}
          />
        );
      });
      const completeCells = [
        <StyledTableCell
          align="center"
          key={`${selectedClientModel}-external-effect-time-period-${timePeriod}`}
          style={{
            fontSize: TABLE_BODY_CELL_FONT_SIZE,
            color: weekNumber <= currentWeek ? "grey" : "black"
          }}
        >
          {timePeriod}
        </StyledTableCell>,
        ...cells
      ];
      return (
        <StyledTableRow
          sx={{
            "& > *": { borderBottom: "unset" },
            "&:last-child td, &:last-child th": { border: 0 }
          }}
          key={`${selectedClientModel}-external-effect-${timePeriod}`}
        >
          {completeCells}
        </StyledTableRow>
      );
    });
  }, [clientExternalEffects, modelVars, extVariableValues, currentWeek]);

  console.log({ extVariableValues });

  const selectedClientName = useMemo(() => {
    return (
      clients?.find((client) => client.id.toString() === selectedClient)
        ?.label ||
      clients?.find((client) => client.id.toString() === selectedClient)
        ?.name ||
      ""
    );
  }, [clients, selectedClient]);

  const selectedModelName = useMemo(() => {
    return modelMapping[selectedClientModel]?.name || "";
  }, [modelMapping, selectedClientModel]);

  const modelVarsWithIsCustom = useMemo(() => {
    return (
      modelVars?.map((modelVar) => ({ ...modelVar, is_custom: false })) || []
    );
  }, [modelVars]);

  return (
    <Box className="w-full p-4">
      <AdminPanelNavBar activeTab="externalEffects" />
      <Box className="flex justify-center w-full p-2 md:p-8 mt-8 md:mt-16">
        <CustomSnackBar
          id={snackbarAlert.message}
          key={snackbarAlert.message}
          open={snackbarAlert.open}
          message={snackbarAlert.message}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          autoHideDuration={snackbarAlert.autoHideDuration}
          color={snackbarAlert.severity}
        />
        <Paper className="w-full">
          <Box className="flex justify-center w-full md:w-1/2 p-8">
            <ClientSelector
              selectedClient={selectedClient}
              setSelectedClient={onClientSelectChange}
            />
            <Box className="w-full pl-2">
              {selectedClient.length > 0 && (
                <ClientModelSelector
                  selectedClient={selectedClient}
                  selectedClientModel={selectedClientModel}
                  setSelectedClientModel={setSelectedClientModel}
                />
              )}
            </Box>
          </Box>
          <ModelVarsCsvReader
            enabled={selectedClientModel.length > 0 && !isLoadingExtVars}
            modelVars={modelVarsWithIsCustom}
            timePeriods={clientExternalEffects?.time_periods || []}
            templateFileName={`${selectedClientName}_${selectedModelName}_external_effects_template.csv`}
            setModelVars={setModelVars}
            setVariableValues={setExtVariableValues}
            modelHeader="external_effect"
          >
            <TableContainer
              className="w-full mt-8 overflow-y-scroll"
              sx={{ maxHeight: "calc(100vh - 450px)", minHeight: 300 }}
            >
              <Table stickyHeader aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    {columns.map((column, index) => (
                      <StyledTableCell
                        key={column.id}
                        align={column.align}
                        style={{
                          minWidth: column.minWidth,
                          borderRight:
                            index === columns.length - 1
                              ? "none"
                              : "1px solid #caccce",
                          fontSize: TABLE_HEADER_CELL_FONT_SIZE
                        }}
                      >
                        {column.label}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingExternalData ? (
                    <TableRowsSkeleton
                      id={`client-${selectedClient}-${selectedClientModel}-external-effects-skeleton`}
                      numberOfRows={5}
                      columns={columns.length}
                    />
                  ) : (
                    tableRows
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ModelVarsCsvReader>
          <Box className="flex justify-end p-8 gap-4">
            <Tooltip title={t("common.discard")}>
              <IconButton
                color="error"
                onClick={onDiscardClick}
                disabled={
                  isLoadingExternalData ||
                  isLoading ||
                  selectedClientModel.length === 0 ||
                  isSaving
                }
              >
                <CancelIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.save")}>
              <IconButton
                color="success"
                onClick={onSaveClick}
                disabled={
                  isLoadingExternalData ||
                  isLoading ||
                  selectedClientModel.length === 0 ||
                  isSaving
                }
              >
                <SaveIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
              </IconButton>
            </Tooltip>
            <ConfirmationDialog
              open={confirmationDialogStatus.open}
              onClose={onCloseConfirmationDialog}
              onConfirm={onConfirmConfirmationDialog}
              title={confirmationDialogStatus.title}
              buttonColor={confirmationDialogStatus.buttonColor}
              confirmButtonText={confirmationDialogStatus.confirmButtonText}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ViewClientExternalEffects;
